import { contractorDisplayName } from '@/utils/contractorDisplayName';

export const TOOL_LABELS: Record<string, string> = {
  queryData: 'Запрос данных',
  queryCapabilities: 'Справочник полей',
  batchLookup: 'Пакетный поиск',
  batchWrite: 'Пакетная запись',
  createProject: 'Создание проекта',
  createAuthor: 'Создание автора',
  createReader: 'Создание чтеца',
  editReader: 'Редактирование чтеца',
  createContractor: 'Создание контрагента',
  editContractor: 'Редактирование контрагента',
  createContractorService: 'Добавление услуги контрагенту',
  editContractorService: 'Редактирование услуги контрагента',
  createDepartment: 'Создание подразделения',
  createEdition: 'Создание редакции',
  createEditor: 'Создание редактора',
  editEdition: 'Редактирование редакции',
  editEditor: 'Редактирование редактора',
  createProducer: 'Создание продюсера',
  editProducer: 'Редактирование продюсера',
  createSoundEngineer: 'Создание звукорежиссёра',
  createTecheditor: 'Создание техредактора',
  createCorrector: 'Создание корректора',
  editSoundEngineer: 'Редактирование звукорежиссёра',
  editCorrector: 'Редактирование корректора',
  createUser: 'Создание пользователя',
  editUser: 'Редактирование пользователя',
  deleteUser: 'Удаление пользователя',
  editPerson: 'Редактирование персоны',
  deletePerson: 'Удаление персоны',
  createSession: 'Создание сессии',
  editProject: 'Редактирование проекта',
  editProjectReader: 'Чтец по проекту',
  editSession: 'Редактирование сессии',
  restoreProject: 'Восстановление проекта',
  restoreSession: 'Восстановление сессии',
  addSharedStressWords: 'Общий словарь ударений',
  saveUserMemory: 'Память',
  listUserMemory: 'Память',
  replaceUserMemorySlot: 'Память',
  deleteUserMemorySlot: 'Память',
};

export function getToolLabel(toolName: string): string {
  return TOOL_LABELS[toolName] ?? toolName;
}

export const QUERY_LABELS: Record<string, string> = {
  projects: 'проекты',
  sessions: 'сессии',
  readers: 'чтецы',
  authors: 'авторы',
  users: 'пользователи',
  contractors: 'контрагенты',
  contractor_services: 'услуги контрагентов',
  departments: 'подразделения',
  editions: 'редакции',
  editors: 'редакторы',
  producers: 'продюсеры',
  soundengineers: 'звукорежиссёры',
  techeditors: 'техредакторы',
  correctors: 'корректоры',
  reader_free_slots: 'свободные слоты чтецов',
  studio_free_slots: 'свободные слоты студий',
  project_status_events: 'события статусов проекта',
};

export function describeQueryFilters(filters: Record<string, unknown> | undefined): string {
  if (!filters || typeof filters !== 'object') return '';
  const parts: string[] = [];
  if (filters.projectName && String(filters.projectName).trim()) parts.push(`название «${String(filters.projectName).trim()}»`);
  if (filters.projectId) parts.push('проект');
  if (filters.readerId) parts.push('чтец');
  if (filters.readerName && String(filters.readerName).trim()) parts.push(`чтец «${String(filters.readerName).trim()}»`);
  if (filters.readerNotes && String(filters.readerNotes).trim()) parts.push('заметки чтеца');
  if (filters.contractorId) parts.push('студия');
  if (filters.contractorName && String(filters.contractorName).trim()) parts.push(`контрагент «${String(filters.contractorName).trim()}»`);
  if (filters.contractorNotes && String(filters.contractorNotes).trim()) parts.push('заметки контрагента');
  if (filters.authorName && String(filters.authorName).trim()) parts.push(`автор «${String(filters.authorName).trim()}»`);
  if (filters.userName && String(filters.userName).trim()) parts.push(`пользователь «${String(filters.userName).trim()}»`);
  if (filters.editionName && String(filters.editionName).trim()) parts.push(`редакция «${String(filters.editionName).trim()}»`);
  if (filters.editorName && String(filters.editorName).trim()) parts.push(`редактор «${String(filters.editorName).trim()}»`);
  if (filters.departmentCode && String(filters.departmentCode).trim()) parts.push(`подразделение «${String(filters.departmentCode).trim()}»`);
  if (filters.producerName && String(filters.producerName).trim()) parts.push(`продюсер «${String(filters.producerName).trim()}»`);
  if (filters.soundengineerName && String(filters.soundengineerName).trim()) parts.push(`звукорежиссёр «${String(filters.soundengineerName).trim()}»`);
  if (filters.techeditorName && String(filters.techeditorName).trim()) parts.push(`техредактор «${String(filters.techeditorName).trim()}»`);
  if (filters.correctorName && String(filters.correctorName).trim()) parts.push(`корректор «${String(filters.correctorName).trim()}»`);
  if (filters.state && String(filters.state).trim()) parts.push(`статус «${String(filters.state).trim()}»`);
  if (filters.eventType && String(filters.eventType).trim()) parts.push(`событие «${String(filters.eventType).trim()}»`);
  if (filters.from && filters.to) parts.push(`период ${String(filters.from)}–${String(filters.to)}`);
  if (filters.date && String(filters.date).trim()) parts.push(`дата ${String(filters.date)}`);
  if (filters.start && filters.end) parts.push(`слот ${String(filters.start)}–${String(filters.end)}`);
  return parts.length > 0 ? parts.join(', ') : '';
}

export function formatQueryDataSummary(
  input: { query?: string; filters?: Record<string, unknown> } | undefined,
  output: unknown
): string {
  const count = Array.isArray(output) ? output.length : 0;
  const countStr = count === 1 ? '1 запись' : count > 1 ? `${count} записей` : '0 записей';
  if (!input?.query) return `Поиск: ${countStr}`;
  const queryLabel = QUERY_LABELS[input.query] ?? input.query;
  const filterDesc = describeQueryFilters(input.filters);
  const byClause = filterDesc ? ` по ${filterDesc}` : '';
  return `Поиск ${queryLabel}${byClause}: ${countStr}`;
}

export function getQueryTypeFromInput(input: unknown): string | null {
  const inp = input as { query?: string } | undefined;
  return inp && typeof inp === 'object' && typeof inp.query === 'string' ? inp.query : null;
}

export const SUCCESS_MESSAGES: Record<string, string> = {
  queryData: 'Выполнен поиск',
  batchLookup: 'Поиск выполнен',
  batchWrite: 'Операции выполнены',
  createProject: 'Проект создан',
  createAuthor: 'Автор добавлен',
  createReader: 'Чтец добавлен',
  editReader: 'Чтец обновлён',
  createContractor: 'Контрагент добавлен',
  editContractor: 'Контрагент обновлён',
  createContractorService: 'Услуга контрагента добавлена',
  editContractorService: 'Услуга контрагента обновлена',
  createDepartment: 'Подразделение создано',
  createEdition: 'Редакция создана',
  createEditor: 'Редактор добавлен',
  editEdition: 'Редакция обновлена',
  editEditor: 'Редактор обновлён',
  createProducer: 'Продюсер добавлен',
  editProducer: 'Продюсер обновлён',
  createSoundEngineer: 'Звукорежиссёр добавлен',
  createTecheditor: 'Техредактор добавлен',
  createCorrector: 'Корректор добавлен',
  editSoundEngineer: 'Звукорежиссёр обновлён',
  editCorrector: 'Корректор обновлён',
  createUser: 'Пользователь добавлен',
  editUser: 'Пользователь обновлён',
  deleteUser: 'Пользователь удалён',
  editPerson: 'Персона обновлена',
  deletePerson: 'Персона удалена',
  createSession: 'Сессия создана',
  editSession: 'Сессия обновлена',
  editProject: 'Проект обновлён',
  editProjectReader: 'Данные чтеца по проекту обновлены',
  restoreProject: 'Проект восстановлен',
  restoreSession: 'Сессия восстановлена',
  saveUserMemory: 'Память обновлена',
  replaceUserMemorySlot: 'Память обновлена',
  deleteUserMemorySlot: 'Память обновлена',
  listUserMemory: 'Память обновлена',
};

const BUTTONS_REGEX = /\[КНОПКИ:([^\]]+)\]/g;

export function parseMessageWithButtons(text: string): { text: string; buttons: string[] } {
  const buttons: string[] = [];
  let match: RegExpExecArray | null;
  const regex = new RegExp(BUTTONS_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    const labels = match[1].split('|').map((s) => s.trim()).filter(Boolean);
    buttons.push(...labels);
  }
  const cleanText = text.replace(BUTTONS_REGEX, '').trim().replace(/\n{2,}/g, '\n\n');
  return { text: cleanText, buttons };
}

export function isQueryDataGroupable(summary: string): boolean {
  return summary.startsWith('Поиск ') || summary === 'Выполнен поиск' || summary === 'Данные получены';
}

export type ToolPart = {
  type?: string;
  toolName?: string;
  state?: string;
  output?: unknown;
  input?: unknown;
  errorText?: string;
};

export function formatToolResult(output: unknown, toolName?: string, input?: unknown): string {
  if (output == null) return SUCCESS_MESSAGES[toolName ?? ''] ?? 'Готово';
  if (typeof output === 'object' && output !== null && 'error' in output) {
    const err = String((output as { error: string }).error);
    return err.startsWith('Ошибка') ? err : `Ошибка: ${err}`;
  }
  if (toolName === 'batchLookup' && typeof output === 'object' && output !== null) {
    const entries = Object.values(output as Record<string, { found?: boolean }>).filter(
      (v) => v && typeof v === 'object' && 'found' in v
    );
    const total = entries.length;
    const found = entries.filter((e) => e.found).length;
    if (total === 0) return 'Поиск выполнен';
    return total === 1 ? (found ? 'Поиск: найдено' : 'Поиск: не найдено') : `Поиск ${total} объектов: найдено ${found}`;
  }
  if (toolName === 'batchWrite' && typeof output === 'object' && output !== null) {
    const o = output as { allSuccess?: boolean; results?: Array<{ success?: boolean; op?: string }> };
    const total = o.results?.length ?? 0;
    const successCount = o.results?.filter((r) => r.success).length ?? 0;
    if (total === 0) return 'Выполнено';
    return successCount === total ? `Выполнено ${total} операций` : `Выполнено ${successCount} из ${total} операций`;
  }
  if (toolName === 'addSharedStressWords' && typeof output === 'object' && output !== null) {
    const o = output as { confirmed?: boolean; words?: unknown[]; added?: unknown[] };
    if (o.confirmed === false) return `Слова разобраны: ${o.words?.length ?? 0}`;
    const added = o.added?.length ?? 0;
    return added > 0 ? `В общий словарь добавлено: ${added}` : 'Ничего не добавлено';
  }
  if (toolName === 'queryData' && input != null) {
    return formatQueryDataSummary(input as { query?: string; filters?: Record<string, unknown> }, output);
  }
  if (typeof output === 'object' && output !== null && 'success' in output) {
    const o = output as {
      success?: boolean;
      author?: { name: string };
      reader?: { name: string };
      contractor?: { name?: string | null; legal_entity?: string | null };
      contractor_service?: { service: string };
      soundengineer?: { name: string };
      techeditor?: { name: string };
      corrector?: { name: string };
      project?: { name: string };
      producer?: { name: string };
      edition?: { name: string };
      editor?: { name: string };
      department?: { code: string };
      person?: { name: string };
      session?: unknown;
      updated?: boolean;
    };
    if (o.success && o.author) return `Автор ${o.author.name} добавлен`;
    if (o.success && o.reader) return `Чтец ${o.reader.name} добавлен`;
    if (o.success && o.soundengineer) return `Звукорежиссёр ${o.soundengineer.name} добавлен`;
    if (o.success && o.techeditor) return `Техредактор ${o.techeditor.name} добавлен`;
    if (o.success && o.corrector) return toolName === 'editCorrector' ? `Корректор ${o.corrector.name} обновлён` : `Корректор ${o.corrector.name} добавлен`;
    if (o.success && o.contractor && o.updated) {
      const label = contractorDisplayName(o.contractor);
      return label ? `Контрагент ${label} обновлён` : 'Контрагент обновлён';
    }
    if (o.success && o.contractor) {
      const label = contractorDisplayName(o.contractor);
      return label ? `Контрагент ${label} добавлен` : 'Контрагент добавлен';
    }
    if (o.success && o.contractor_service && o.updated) return `Услуга «${o.contractor_service.service}» обновлена`;
    if (o.success && o.contractor_service) return `Услуга «${o.contractor_service.service}» добавлена`;
    if (o.success && o.project && toolName === 'editProjectReader') {
      return `Проект ${o.project.name}: данные чтеца по проекту обновлены`;
    }
    if (o.success && o.project && toolName === 'restoreProject') {
      return o.project.name ? `Проект ${o.project.name} восстановлен` : 'Проект восстановлен';
    }
    if (o.success && o.project && toolName === 'editProject') {
      return `Проект ${o.project.name} обновлён`;
    }
    if (o.success && o.project) return `Проект ${o.project.name} создан`;
    if (o.success && o.producer) return toolName === 'editProducer' ? `Продюсер ${o.producer.name} обновлён` : `Продюсер ${o.producer.name} добавлен`;
    if (o.success && o.edition) return toolName === 'editEdition' ? `Редакция ${o.edition.name} обновлена` : `Редакция ${o.edition.name} создана`;
    if (o.success && o.editor) return toolName === 'editEditor' ? `Редактор ${o.editor.name} обновлён` : `Редактор ${o.editor.name} добавлен`;
    if (o.success && o.department) return `Подразделение ${o.department.code} создано`;
    if (o.success && o.session) {
      if (toolName === 'restoreSession') return 'Сессия восстановлена';
      return toolName === 'editSession' ? 'Сессия обновлена' : 'Сессия создана';
    }
    if (o.success && toolName === 'deleteUser' && 'message' in o) return String((o as { message?: string }).message ?? 'Пользователь удалён');
    if (o.success && toolName === 'deletePerson' && 'message' in o) return String((o as { message?: string }).message ?? 'Персона удалена');
    if (o.success && o.person && toolName === 'editPerson') return `Персона ${o.person.name} обновлена`;
    if (o.success) return SUCCESS_MESSAGES[toolName ?? ''] ?? 'Готово';
  }
  if (Array.isArray(output) && toolName === 'queryData' && input != null) {
    return formatQueryDataSummary(input as { query?: string; filters?: Record<string, unknown> }, output);
  }
  if (Array.isArray(output)) return SUCCESS_MESSAGES[toolName ?? ''] ?? 'Данные получены';
  return SUCCESS_MESSAGES[toolName ?? ''] ?? 'Готово';
}

export function getToolSummary(part: ToolPart): string | null {
  if (part.state !== 'output-available' && part.state !== 'output-error') return null;
  if (!part.type?.startsWith('tool-') && part.type !== 'dynamic-tool') return null;
  const toolName = part.toolName ?? part.type?.replace(/^tool-/, '') ?? '';
  const hasError =
    part.errorText ||
    (typeof part.output === 'object' && part.output !== null && 'error' in part.output);
  if (hasError) {
    const err = part.errorText ?? (typeof part.output === 'object' && part.output !== null && 'error' in part.output
      ? String((part.output as { error: string }).error)
      : null);
    return err ? (err.startsWith('Ошибка') ? err : `Ошибка: ${err}`) : null;
  }
  return formatToolResult(part.output, toolName, part.input);
}

export function isQueryDataSuccess(part: ToolPart, summary: string): boolean {
  const toolName = part.toolName ?? part.type?.replace(/^tool-/, '');
  const isResult = part.state === 'output-available' || part.state === 'output-error';
  const hasError =
    part.errorText ||
    (typeof part.output === 'object' && part.output !== null && 'error' in part.output);
  return (
    (part.type?.startsWith('tool-') || part.type === 'dynamic-tool') &&
    toolName === 'queryData' &&
    isResult &&
    !hasError &&
    isQueryDataGroupable(summary)
  );
}
